import { setLeaseCompMedia } from "@/features/leaseMedia/leaseMediaSlice";
import { setPropertyMedia } from "@/features/media/mediaSlice";
import type { GetMediaByIdsResponse } from "@/features/media/types/media.types";
import type { Survey } from "@/features/survey/types";
import { formatDateMapAPI } from "@/shared/utils/map";
import { mediaApi } from "./base";
import { getSurveyId } from "./interactiveSurveyAuth";

// TypeScript interfaces following GitHub Copilot conventions
interface PdfGenerationRequest {
  readonly surveyId: string;
  readonly name: string;
  readonly isLandscape: boolean;
  readonly sectionInfoList: readonly {
    readonly name: string;
    readonly link: string;
  }[];
  readonly isShortlisted?: boolean;
}

interface PdfGenerationResponse {
  readonly success: boolean;
  readonly message?: string;
  readonly jobId?: string;
  readonly estimatedCompletionTime?: string;
}

// Interface for PDF URL response following TypeScript conventions
interface PdfUrlResponse {
  readonly id: string;
  readonly surveyId: string;
  readonly fileUrl: string;
  readonly state: number;
  readonly isShortlisted: boolean;
  readonly createdOn: string;
  readonly updatedOn?: string;
}

// Custom error class for PDF generation errors following GitHub Copilot patterns
export class PdfGenerationError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "PdfGenerationError";
  }
}

/**
 * Media type interface representing returned media items from the API
 */
export interface MediaItem {
  cloudfrontUrl: string;
  createdOn: string;
  displayName: string;
  hashCode: string;
  isHidden: boolean;
  keyName: string;
  mediaId: string;
  mediaType: string;
  modifiedFromMediaId?: string;
  order: boolean;
  status: string;
  surveyId: string;
  user: string;
}

export interface PropertyImage {
  cloudfrontUrl: string;
}

/**
 * Media type enum for identifying media categories
 */
export enum MediaType {
  CLIENTLOGO = "CLIENTLOGO",
  IMAGES = "IMAGES",
  FLIERS = "FLIERS",
  FLOORPLANS = "FLOORPLANS",
}

/**
 * Interface for media metadata object
 */
interface MediaMetadata {
  readonly surveyId: string;
  readonly mediaType: string;
}

/**
 * Interface for upload request parameters
 */
interface UploadMarketMapRequest {
  readonly file: File;
  readonly media: MediaMetadata;
}

/**
 * Interface for upload response
 */
interface UploadResponse {
  readonly success: boolean;
  readonly message: string;
  readonly mediaId?: string;
  readonly cloudfrontUrl?: string;
}

/**
 * Enhanced media API with endpoints for retrieving different media types
 */
export const surveyMediaApi = mediaApi
  .enhanceEndpoints({
    addTagTypes: ["Media"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       *  Trigger market map generation and poll for completion
       */
      marketMapCallPoll: build.mutation<Survey, string>({
        query: (surveyId) => ({
          url: `api/media/pollMarketMap`,
          method: "POST",
          body: {
            surveyId,
            marketMapCreatedOn: formatDateMapAPI(new Date()),
          },
        }),
      }),

      /**
       * Named mutation for PDF generation following async/await patterns
       */
      generateSurveyPdf: build.mutation<
        PdfGenerationResponse,
        PdfGenerationRequest
      >({
        query: (pdfRequest) => ({
          url: "/api/fileGeneration/processPdfAsync",
          method: "POST",
          body: pdfRequest,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Media", id: surveyId },
        ],
        // Transform error response following GitHub Copilot error handling patterns
        transformErrorResponse: (response: any) => {
          const statusCode = response.status;
          const errorData = response.data;

          let errorMessage = "Failed to generate PDF";

          if (statusCode === 400) {
            errorMessage = "Invalid PDF generation request";
          } else if (statusCode === 401) {
            errorMessage = "Unauthorized access to PDF generation service";
          } else if (statusCode === 403) {
            errorMessage = "Insufficient permissions for PDF generation";
          } else if (statusCode === 404) {
            errorMessage = "PDF generation service not found";
          } else if (statusCode === 429) {
            errorMessage =
              "Too many PDF generation requests. Please try again later";
          } else if (statusCode >= 500) {
            errorMessage = "Internal server error during PDF generation";
          }

          return new PdfGenerationError(
            errorData?.message || errorMessage,
            statusCode,
            errorData,
          );
        },
      }),

      /**
       * Get PDF URL for survey with proper error handling and caching
       * Following GitHub Copilot conventions for API queries
       */
      getPdfUrl: build.query<PdfUrlResponse[], string>({
        query: (surveyId) => ({
          url: `api/fileGeneration/survey/${surveyId}`,
          method: "GET",
        }),
        providesTags: (_result, _error, surveyId) => [
          { type: "Media", id: `pdf-${surveyId}` },
        ],
      }),

      /**
       * Upload Market Map file with proper FormData handling
       * Accepts file and media metadata object
       */
      uploadMarketMapFile: build.mutation<
        UploadResponse,
        UploadMarketMapRequest
      >({
        query: ({ file, media }) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("media", JSON.stringify(media));
          return {
            url: `api/media/uploadMarketMap`,
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: ["Media"],
      }),
      /**
       * Get client logo for survey cover page
       */
      getClientLogo: build.query<MediaItem[], string>({
        query: (surveyId) => ({
          url: `/api/media/${surveyId}/CLIENTLOGO/active`,
          headers: {
            surveyId: surveyId,
          },
        }),
        providesTags: ["Media"],
        // Transform response to handle the expected {media: []} structure
        transformResponse: (response: unknown) => {
          // If response is null or undefined, return empty array
          if (response === null || response === undefined) {
            return [];
          }

          // If response is already an array of MediaItems, return it
          if (Array.isArray(response)) {
            return response;
          }

          // If response has a 'media' property that's an array, return it
          if (
            typeof response === "object" &&
            response !== null &&
            "media" in response
          ) {
            const media = (response as { media: unknown }).media;
            if (Array.isArray(media)) {
              return media as MediaItem[];
            }
          }

          // Otherwise, return empty array
          return [];
        },
      }),

      /**
       * Get property main image for market map and comparison pages
       */
      getPropertyImage: build.query<PropertyImage, string>({
        query: (propertyId) => ({
          url: `/api/media/property/image/${propertyId}`,
        }),
        providesTags: ["Media"],
        // Handle empty responses and spaces in URLs
        transformResponse: (response: PropertyImage, meta) => {
          // Check for empty response by inspecting content length
          if (meta?.response?.headers?.get("content-length") === "0") {
            // Return a fallback image URL
            return { cloudfrontUrl: "/image-placeholder-medium.png" };
          }
          // Handle spaces in cloudfrontUrl
          if (response && response.cloudfrontUrl) {
            // Encode URLs with spaces or parentheses to ensure proper formatting
            return {
              cloudfrontUrl:
                response.cloudfrontUrl.includes(" ") ||
                response.cloudfrontUrl.includes("(") ||
                response.cloudfrontUrl.includes(")")
                  ? encodeURI(response.cloudfrontUrl)
                  : response.cloudfrontUrl,
            };
          }
          return response;
        },
      }),

      /**
       * Get property media by type (images, fliers, floor plans)
       */
      getPropertyMedia: build.query<
        MediaItem[],
        { propertyId: string; mediaType: MediaType }
      >({
        query: ({ propertyId, mediaType }) => {
          const surveyId = getSurveyId();
          return {
            url: `/api/media/${surveyId}/${propertyId}/${mediaType}/active`,
          };
        },
        providesTags: ["Media"],
        // Transform response to ensure we always return an array
        transformResponse: (response: unknown) => {
          // If response is null or undefined, return empty array
          if (response === null || response === undefined) {
            return [];
          }

          // If response is already an array of MediaItems, return it
          if (Array.isArray(response)) {
            return response;
          }

          // If response has a 'media' property that's an array, return it
          if (
            typeof response === "object" &&
            response !== null &&
            "media" in response
          ) {
            const media = (response as { media: unknown }).media;
            if (Array.isArray(media)) {
              return media as MediaItem[];
            }
          }

          // Otherwise, return empty array
          return [];
        },
      }),

      getMediaByIds: build.query<
        GetMediaByIdsResponse,
        { ids: string[]; propertyType?: string }
      >({
        query: ({ ids, propertyType }) => ({
          url: `api/media/getMediaByIds`,
          method: "POST",
          body: { ids, propertyType },
        }),
        providesTags: (result, _error, { ids }) =>
          result
            ? [
                ...ids.map((id: string) => ({ type: "Media" as const, id })),
                { type: "Media", id: "LIST" },
              ]
            : [{ type: "Media", id: "LIST" }],
        async onQueryStarted({ propertyType }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data?.propertyMedia) {
              // Use setPropertyMedia to update the entire propertyMedia object efficiently
              if (propertyType === "LEASE_COMP") {
                dispatch(setLeaseCompMedia(data.propertyMedia));
              } else {
                dispatch(setPropertyMedia(data.propertyMedia));
              }
            }
          } catch (error) {
            // Handle error if needed
            console.error("Failed to fetch media by IDs:", error);
          }
        },
      }),

      getEdpTransformedImage: build.query<Blob, { imageUrl: string }>({
        query: ({ imageUrl }) => ({
          url: `api/media/edp/attachment?filename=${encodeURIComponent(imageUrl)}`,
          method: "GET",
          responseHandler: (response) => response.blob(),
        }),
      }),
    }),
  });

export const {
  useGetClientLogoQuery,
  useGetPropertyImageQuery,
  useGetPropertyMediaQuery,
  useMarketMapCallPollMutation,
  useGetPdfUrlQuery,
  useUploadMarketMapFileMutation,
  useGenerateSurveyPdfMutation,
  useGetMediaByIdsQuery,
  useGetEdpTransformedImageQuery,
} = surveyMediaApi;
